import { ColumnHelper } from "@tanstack/react-table";
import isEmpty from "lodash/isEmpty";
import { ActionIcon } from "PFComponents/action_icon";
import { Inline, Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { MultiToggle } from "PFComponents/multi_toggle";
import Table from "PFComponents/table";
import { Typography } from "PFComponents/typography";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { Audience } from "PFCore/types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AudienceSelector } from "./audience_selector";
import css from "./privacy_selector.module.scss";
import { Privacy } from "./privacy_selector.types";

type PrivacySelectorProps = {
  isDisabled?: boolean;
  isPrivate: boolean;
  isNew: boolean;
  audience: Audience[];
  onAudiencesChange: (audience: Audience[]) => void;
  onPrivateChange: (id: Privacy) => void;
};

export const PrivacySelector = ({
  isDisabled,
  isPrivate,
  isNew,
  audience,
  onAudiencesChange,
  onPrivateChange
}: PrivacySelectorProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.privacy" });

  const { customTypes } = useCustomTypes();
  const broadcastTypes = useMemo(
    () => customTypes.filter((ct) => ct.sections.find((section) => section.key === "broadcast")),
    [customTypes]
  );
  const isAudienceAllowed = broadcastTypes.length > 0;

  const options = [
    {
      id: Privacy.Public,
      value: t("public"),
      title: t("public"),
      disabled: isDisabled
    },
    {
      id: Privacy.Private,
      value: t("private"),
      title: t("private"),
      disabled: isDisabled
    }
  ];

  const columns = (columnHelper: ColumnHelper<Audience>) => [
    ...broadcastTypes.map(({ name, display_as }) =>
      columnHelper.accessor(name, {
        id: name,
        header: () => (
          <div className={css.columnHeader} title={display_as}>
            {display_as}
          </div>
        ),
        size: 140,
        cell: (info) => {
          const customValues = info.getValue() || [];
          return (
            <Inline alignItems="center">
              <Typography variant="bodyRegular">{customValues.map(({ text }) => text).join(", ")}</Typography>
            </Inline>
          );
        }
      })
    ),
    columnHelper.accessor("unconfirmed", {
      id: "unconfirmed",
      header: "",
      size: 32,
      meta: { sticky: true, cellClassName: css.deleteIconCell },
      cell: (info) =>
        info.getValue() || isNew ? (
          <Inline justifyContent="end">
            <ActionIcon
              name="remove"
              size="sm"
              color="paletteRed1"
              onClick={() => onAudiencesChange(audience.filter((item) => info.row.original !== item))}
              disabled={isDisabled}
            />
          </Inline>
        ) : null
    })
  ];

  return (
    <>
      <Stack justifyContent="start">
        <MultiToggle<Privacy>
          options={options}
          controlledValue={isPrivate ? Privacy.Private : Privacy.Public}
          onChange={onPrivateChange}
          fluid
        />
        <Typography className={css.note} variant="labelRegular">
          {t("note")}
        </Typography>
      </Stack>
      {isPrivate && isAudienceAllowed && (
        <div>
          {!isEmpty(audience) && (
            <>
              <Table<Audience>
                tableData={audience}
                generateColumns={columns}
                enableSorting={false}
                disableAutoResizing
                topAlignContent
              />
              <Divider color="paletteNeutral1" className={css.divider} />
            </>
          )}
          <Typography variant="bodyBold" withMargin>
            {t("selectAudienceGroups")}
          </Typography>
          <AudienceSelector
            audience={audience}
            broadcastTypes={broadcastTypes}
            onAudienceChange={(newAudience) =>
              onAudiencesChange([...audience, { ...newAudience, unconfirmed: true } as Audience])
            }
            disabled={isDisabled}
          />
        </div>
      )}
    </>
  );
};
